import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Carousel,
  Button,
  Card,
  Image,
  Placeholder,
  Tabs,
  Tab,
  Badge
} from 'react-bootstrap';
import { ArrowLeft, Calendar, CalendarCheck, AlertTriangle, Info } from 'lucide-react';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { app } from '../FireBase';
import '../css/RoomDetailsPage.css';
import Amenities from '../components/Amenities';
import RelatedPackages from '../components/relatedPackages';
import TourSearchModal from '../components/TourSearchModal';
import ProfileReminder from '../components/ProfileReminder';
import { AuthContext } from "../context/AuthContext";

const db = getFirestore(app);
const STORAGE_KEY = 'tourBookingSearch';

export default function PackageDetailsPage() {
  const { id } = useParams();
  const { currentUser } = useContext(AuthContext);
  const navigate = useNavigate();

  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState({ from: null, to: null });
  const [index, setIndex] = useState(0);
  const [imgLoaded, setImgLoaded] = useState({});
  const [searchModal, setSearchModal] = useState(false);
  const [showSticky, setShowSticky] = useState(false);
  const [showReminder, setShowReminder] = useState(false);
  const [members, setMembers] = useState(0);
  const [days, setDays] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const ref = doc(db, 'packages', id);
        const snap = await getDoc(ref);

        if (snap.exists()) {
          const data = { id: snap.id, ...snap.data() };
          setPkg(data);
          setMembers(Number(0));
          setDays(Number( 0));
        }
      } catch (error) {
        console.error('Error fetching package:', error);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  useEffect(() => {
    const sentinel = document.getElementById('scroll-sentinel');
    if (!sentinel) return;

    const obs = new IntersectionObserver(
      ([entry]) => setShowSticky(!entry.isIntersecting),
      { threshold: 0 }
    );

    obs.observe(sentinel);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) return;

    try {
      const parsed = JSON.parse(saved);

      setRange({
        from: parsed?.from ? new Date(parsed.from) : null,
        to: parsed?.to ? new Date(parsed.to) : null,
      });

      if (parsed?.days) {
        setDays(Number(parsed.days));
      }

      if (parsed?.members) {
        setMembers(Number(parsed.members));
      }
    } catch (error) {
      console.error('Error parsing saved package search:', error);
    }
  }, [searchModal]);

  const basePrice = Number(pkg?.price || pkg?.pricePerPerson || 0);
  const totalPrice = basePrice * Number(members) * Number(days);

  const calculatedEndDate = range.from
    ? new Date(new Date(range.from).setDate(new Date(range.from).getDate() + Number(days) - 1))
    : null;

  const images = Array.isArray(pkg?.images) ? pkg.images : [];

  if (loading) {
    return (
      <Container
        fluid
        className="p-0"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          overflow: "hidden",
          background: "#fff",
          transition: "opacity 0.3s ease",
        }}
      >
        <Container className="px-3 py-5">
          <Row className="gy-4">
            <Col lg={7}>
              <div className="room-carousel-wrapper">
                <div
                  style={{
                    width: "100%",
                    height: 250,
                    borderRadius: "15px",
                    backgroundColor: "#e9ecef",
                  }}
                />
                <div className="d-flex gap-2 mt-3">
                  {[...Array(4)].map((_, i) => (
                    <div
                      key={i}
                      style={{
                        width: 90,
                        height: 70,
                        borderRadius: "8px",
                        backgroundColor: "#e9ecef",
                      }}
                    />
                  ))}
                </div>
              </div>
            </Col>

            <Col lg={5}>
              <Card className="p-4 shadow-sm border-0">
                <div
                  style={{
                    width: "100%",
                    height: 50,
                    borderRadius: "8px",
                    backgroundColor: "#e9ecef",
                    marginBottom: "15px",
                  }}
                />
                <div
                  style={{
                    width: "100%",
                    height: 180,
                    borderRadius: "8px",
                    backgroundColor: "#e9ecef",
                    marginBottom: "15px",
                  }}
                />
                <div
                  style={{
                    width: "100%",
                    height: 45,
                    borderRadius: "8px",
                    backgroundColor: "#e9ecef",
                    marginTop: "20px",
                  }}
                />
              </Card>
            </Col>
          </Row>
        </Container>
      </Container>
    );
  }

  if (!pkg) {
    return (
      <Container className="py-5 text-center">
        <h4>Package not found</h4>
        <Button variant="link" onClick={() => navigate(-1)}>
          <ArrowLeft size={16} /> Back
        </Button>
      </Container>
    );
  }

  return (
    <Container fluid className="p-0">
      <Container className="px-3">
        <div id="scroll-sentinel" style={{ height: 1 }} />

        <Row className="gy-4">
          <Col lg={7}>
            <div className="room-carousel-wrapper">
              <Carousel
                activeIndex={index}
                onSelect={(i) => setIndex(i)}
                variant="dark"
                className="mb-3 room-main-images"
              >
                {images.map((url, i) => (
                  <Carousel.Item key={i}>
                    {!imgLoaded[`main-${i}`] && (
                      <Placeholder
                        animation="glow"
                        style={{
                          width: '100%',
                          height: 400,
                          borderRadius: '.75rem',
                          backgroundColor: '#e9ecef'
                        }}
                      />
                    )}

                    <img
                      src={url}
                      className="d-block w-100 room-main-image"
                      alt={`Slide ${i + 1}`}
                      onLoad={() =>
                        setImgLoaded((prev) => ({ ...prev, [`main-${i}`]: true }))
                      }
                      style={{ display: imgLoaded[`main-${i}`] ? 'block' : 'none' }}
                    />
                  </Carousel.Item>
                ))}
              </Carousel>
            </div>

            <div className="room-thumbs d-flex gap-2">
              {images.map((url, i) => (
                <div
                  key={i}
                  onClick={() => setIndex(i)}
                  className={`thumb-container ${index === i ? 'thumb-active' : ''}`}
                >
                  {!imgLoaded[`thumb-${i}`] && (
                    <Placeholder
                      animation="glow"
                      style={{
                        width: 90,
                        height: 70,
                        borderRadius: '.5rem',
                        backgroundColor: '#e9ecef'
                      }}
                    />
                  )}

                  <Image
                    src={url}
                    thumbnail
                    onLoad={() =>
                      setImgLoaded((prev) => ({ ...prev, [`thumb-${i}`]: true }))
                    }
                    style={{
                      objectFit: 'cover',
                      display: imgLoaded[`thumb-${i}`] ? 'block' : 'none'
                    }}
                  />
                </div>
              ))}
            </div>
          </Col>

          <Col lg={5}>
            <Card className="room-details-card p-4 shadow-sm border-0">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <Card.Title className="mb-0">{pkg.name}</Card.Title>
                </div>

                <div className="mb-4">
                  <h4 style={{ color: '#54b435' }}>
                    ₹{basePrice.toLocaleString()}
                    <small className="text-muted"> / Person per day</small>
                  </h4>

                  <p className="fw-semibold mt-2 mb-0">
                    Total Price: ₹{totalPrice.toLocaleString()}
                  </p>
                </div>

                <div
                  className="mb-4"
                  dangerouslySetInnerHTML={{ __html: pkg.description }}
                />

                <Card
                  className="stay-info mb-4 border-0 shadow-sm"
                
                  style={{
                    borderRadius: '16px',
                    cursor: 'pointer',
                    background: '#f8fff4',
                  }}
                >
                  <Card.Body className="p-3"   onClick={() => setSearchModal(true)}>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <div>
                        <h6 className="mb-1 fw-bold" style={{ color: '#54b435' }}>
                          Your stay
                        </h6>
                        <small className="text-muted">Choose trip date, days and guests</small>
                      </div>

                      <div
                        style={{
                          width: 42,
                          height: 42,
                          borderRadius: '12px',
                          background: '#eaf7df',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <CalendarCheck size={20} color="#54b435" />
                      </div>
                    </div>

                    <div
                      className="d-flex align-items-start gap-2 mb-3"
                      style={{
                        padding: '12px',
                        borderRadius: '12px',
                        background: '#fff',
                      }}
                    >
                      <CalendarCheck size={18} className="mt-1 text-success" />
                      <div>
                        <div className="fw-semibold">Travel Dates</div>
                        <div className="text-muted small">
                          {range.from ? (
                            <>
                              {range.from.toLocaleDateString()} → {calculatedEndDate?.toLocaleDateString()}
                            </>
                          ) : (
                            'Not selected'
                          )}
                        </div>
                      </div>
                    </div>

                    <Row className="g-2">
                      <Col xs={6}>
                        <div
                          style={{
                            background: '#fff',
                            borderRadius: '12px',
                            padding: '12px',
                            textAlign: 'center',
                          }}
                        >
                          <div className="small text-muted mb-1">Duration</div>
                          <div className="fw-semibold">
                            {days} {days === 1 ? 'Day' : 'Days'}
                          </div>
                        </div>
                      </Col>

                      <Col xs={6}>
                        <div
                          style={{
                            background: '#fff',
                            borderRadius: '12px',
                            padding: '12px',
                            textAlign: 'center',
                          }}
                        >
                          <div className="small text-muted mb-1">Guests</div>
                          <div className="fw-semibold">
                            {members} {members === 1 ? 'Guest' : 'Guests'}
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </Card.Body>
                    <Button
                  size="sm"
                  className="w-100 btn-own-lite"
              
                  onClick={() => {
                    const plannerSection = document.getElementById('day-planner-section');
                    if (plannerSection) {
                      plannerSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }}
                >
                  View Day Planner
                </Button>
                </Card>
                
              

                <Button
                  size="lg"
                  className="w-100 btn-own"
                  onClick={() => {
                    if (!currentUser) {
                      setShowReminder(true);
                      return;
                    }

                    navigate(`/package-checkout/${pkg.id}`, {
                      state: {
                        packageData: pkg,
                        members,
                        days,
                        startDate: range.from,
                        endDate: calculatedEndDate,
                        totalPrice,
                        pricePerPerson: basePrice,
                      },
                    });
                  }}
                  disabled={!(range.from && days > 0 && members > 0)}
                >
                  Book Package
                </Button>

                {( !range.from || days === 0 || members === 0 ) && (
                  <p className="mt-2 mb-0 small text-danger text-center d-flex align-items-center justify-content-center gap-1">
                    {/* <Info size={16} /> */}
                    <span>
                      { !range.from && 'Select trip dates first.' }
                      { range.from && days === 0 && members === 0 && 'Choose your days and guest count to continue.' }
                      { range.from && days === 0 && members > 0 && 'Choose trip duration (days) to continue.' }
                      { range.from && members === 0 && days > 0 && 'Choose guest count to continue.' }
                    </span>
                  </p>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Amenities />

        {pkg.itinerary && pkg.itinerary.length > 0 && (
          <Row className="mt-5" id="day-planner-section">
            <Col>
              <Tabs defaultActiveKey={pkg.itinerary[0]?.day || 'Day 1'} className="mb-3">
                {pkg.itinerary.map((item, idx) => (
                  <Tab eventKey={item.day} title={item.day} key={idx}>
                    <div
                      className="p-3"
                      dangerouslySetInnerHTML={{ __html: item.content }}
                    />
                  </Tab>
                ))}
              </Tabs>
            </Col>
          </Row>
        )}

        <div style={{ marginBottom: '50px' }}>
          <RelatedPackages currentPackageId={pkg.id} />
        </div>

        <TourSearchModal
          show={searchModal}
          onHide={() => setSearchModal(false)}
          durationDays={days}
          members={members}
          minDays={Number(pkg.minDays || 1)}
          maxDays={Number(pkg.maxDays || 7)}
          minMembers={Number(pkg.minMembers || 1)}
          maxMembers={Number(pkg.maxMembers || 10)}
          onApply={({ from, to, days, members }) => {
            setRange({ from, to });
            setDays(days);
            setMembers(members);
          }}
        />

        {/* {showSticky && !searchModal && (
          <div
            className="mobile-book-sticky d-lg-none"
            style={{ borderRadius: '30px 30px 0px 0px' }}
          >
            <Card
              className="p-3 mb-2 bg-white shadow-sm stay-info-mobile"
              style={{ borderRadius: '15px' }}
              onClick={() => setSearchModal(true)}
            >
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="fw-bold text-success">Your stay</span>
                <Badge bg="light" text="success" className="py-1 px-2">
                  <small className="fw-semibold">
                    {members} {members === 1 ? 'Guest' : 'Guests'}
                  </small>
                </Badge>
              </div>

              <div className="small text-muted">
                <div>
                  <strong><Calendar size={14} /></strong>{' '}
                  {range.from
                    ? `${range.from.toLocaleDateString()} → ${calculatedEndDate?.toLocaleDateString()}`
                    : 'Not selected'}
                </div>
                <div>
                  <strong>Days:</strong> {days}
                </div>
              </div>
            </Card>

            <Button
              size="lg"
              className="w-100 mb-3 btn-own"
              onClick={() => {
                if (!currentUser) {
                  setShowReminder(true);
                  return;
                }

                navigate(`/package-checkout/${pkg.id}`, {
                  state: {
                    packageData: pkg,
                    members,
                    days,
                    startDate: range.from,
                    endDate: calculatedEndDate,
                    totalPrice,
                    pricePerPerson: basePrice,
                  },
                });
              }}
              disabled={!range.from}
            >
              Book Now
            </Button>
          </div>
        )} */}
      </Container>

      {showReminder && (
        <ProfileReminder
          show={showReminder}
          onClose={() => setShowReminder(false)}
          type="login"
          auto={false}
        />
      )}
    </Container>
  );
}